import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Context
import { AuthProvider } from './contexts/AuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import TopBar from './components/TopBar';

// Pages
import MainPage from './pages/MainPage';
import AuthPage from './pages/AuthPage';
import ProfilePage from './pages/ProfilePage';
import AddLinksPage from './pages/AddLinksPage';
import AddMidiPage from './pages/AddMidiPage';
import TonalityAddYoutubePage from './pages/TonalityAddYoutubePage';
import CustomYoutubeVideosPage from './pages/CustomYoutubeVideosPage';
import TonalityYoutubeVideoPage from './pages/TonalityYoutubeVideoPage';
import CustomYoutubeVideoViewer from './pages/CustomYoutubeVideoViewer';
import CustomYoutubeQRViewer from './pages/CustomYoutubeQRViewer';
import AdminCustomYoutubeVideos from './pages/AdminCustomYoutubeVideos';
import MidiPlayerPage from './pages/MidiPlayerPage';
import MidiPlayerPianoPage from './pages/MidiPlayerPianoPage';
import MediaFilesPage from './pages/MediaFilesPage';
import ImageUploadPage from './pages/ImageUploadPage';
import ViewMediaPage from './pages/ViewMediaPage';
import SessionManagementPage from './pages/SessionManagementPage';
import AdminPage from './pages/AdminPage';
import AdminDashboard from './components/AdminDashboard';
import FaceRecognitionPage from './pages/FaceRecognitionPage';
import DiagnosticsPage from './pages/DiagnosticsPage';
import LogAnalysisPage from './pages/LogAnalysisPage';
import AlbumCreationPage from './pages/AlbumCreationPage';
import AlbumWizardPage from './pages/AlbumWizardPage';
import AdminAlbumTemplates from './pages/AdminAlbumTemplates';
import AlbumSignupPage from './pages/AlbumSignupPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

// Test Components
import CorsTest from './components/CorsTest';
import EnhancedCorsTest from './components/EnhancedCorsTest';
import NetworkTest from './components/NetworkTest';
import AppInsightsTest from './components/AppInsightsTest';
import AppInsightsDiagnostic from './components/AppInsightsDiagnostic';
import ApplicationInsightsDiagnostic from './components/ApplicationInsightsDiagnostic';

// Services
import appInsightsService from './services/appInsights';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Route change tracking component
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track pageview when route changes
    appInsightsService.trackPageView(
      location.pathname,
      location.pathname + location.search,
      { referrer: document.referrer }
    );
  }, [location]);
  
  return null;
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ErrorBoundary>
          <Router>
            <RouteTracker />
            <TopBar />
            <Routes>
              {/* Public routes */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/login" element={<AuthPage />} /> {/* Redirect alias */}
              <Route path="/signup/album" element={<AlbumSignupPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              
              {/* Protected routes */}
              {/* Main page - accessible to all users */}
              <Route 
                path="/" 
                element={<MainPage />} 
              />
              
              {/* Profile page - any authenticated user */}
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin only pages */}
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AdminPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />

              <Route
                path="/admin/album-templates"
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AdminAlbumTemplates />
                  </ProtectedRoute>
                }
              />
              
              {/* Editor only pages */}
              <Route 
                path="/add-links" 
                element={
                  <ProtectedRoute requiredPermission="editor">
                    <AddLinksPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/add-midi" 
                element={
                  <ProtectedRoute requiredPermission="editor">
                    <AddMidiPage />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/tonality-add-youtube" 
                element={
                  <ProtectedRoute requiredPermission="editor">
                    <TonalityAddYoutubePage />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/media-files" 
                element={
                  <ProtectedRoute requiredPermission="editor">
                    <MediaFilesPage />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/sessions" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <SessionManagementPage />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/image-upload" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <ImageUploadPage />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/image-upload/:sessionId" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <ImageUploadPage />
                  </ProtectedRoute>
                }
              />
              
              <Route 
                path="/tonality-youtube-video" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <TonalityYoutubeVideoPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/custom-youtube-videos" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <CustomYoutubeVideosPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy route for backward compatibility */}
              <Route 
                path="/youtube-videos" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <CustomYoutubeVideosPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/custom-youtube-video/:customVideoId" 
                element={<CustomYoutubeVideoViewer />}
              />
              
              <Route 
                path="/custom-youtube-video/:customVideoId/qr" 
                element={<CustomYoutubeQRViewer />}
              />
              
              {/* Legacy route redirects for backward compatibility */}
              <Route 
                path="/youtube-video/:customVideoId" 
                element={<CustomYoutubeVideoViewer />}
              />
              
              <Route 
                path="/youtube-video/:customVideoId/qr" 
                element={<CustomYoutubeQRViewer />}
              />
              
              <Route 
                path="/admin/custom-youtube" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AdminCustomYoutubeVideos />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/midi-player" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <MidiPlayerPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/midi-player-piano" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <MidiPlayerPianoPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/view-media/:id?" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <ViewMediaPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/face-recognition" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <FaceRecognitionPage />
                  </ProtectedRoute>
                } 
              />

              <Route 
                path="/face-recognition/:sessionId" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <FaceRecognitionPage />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/album/:sessionId" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <AlbumCreationPage />
                  </ProtectedRoute>
                }
              />

              <Route 
                path="/album-wizard/:sessionId" 
                element={
                  <ProtectedRoute requiredPermission="viewer">
                    <AlbumWizardPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Test routes - for development/debugging */}
              <Route 
                path="/cors-test" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <CorsTest />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/cors-test-enhanced" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <EnhancedCorsTest />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/network-test" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <NetworkTest />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/app-insights-test" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AppInsightsTest />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/app-insights-diagnostic" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <AppInsightsDiagnostic />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/app-insights-advanced" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <ApplicationInsightsDiagnostic />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/diagnostics" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <DiagnosticsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/admin/diagnostics" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <DiagnosticsPage />
                  </ProtectedRoute>
                } 
              />
              
              <Route 
                path="/log-analysis" 
                element={
                  <ProtectedRoute requiredPermission="admin">
                    <LogAnalysisPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Google Photos Debug route removed - no longer needed with Photo Picker API */}
            </Routes>
          </Router>
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
